/**
 * UsageDisplay Component
 * 
 * Displays "X / Y analyses used this month" for subscription tracking.
 * Non-blocking UI element that shows usage info without enforcing limits.
 * 
 * Plan Display Names: PRO→"Investor", PRO_PLUS→"Pro"
 */

import React from 'react';
import { BarChart3, Zap, Crown } from 'lucide-react';

// Display name mapping
const getPlanDisplayName = (planType) => {
  const names = { FREE: 'Free', PRO: 'Investor', PRO_PLUS: 'Pro', FOUNDER: 'Founder' };
  return names[planType] || planType;
};

const UsageDisplay = ({ usageInfo, lang = 'en' }) => {
  if (!usageInfo) return null;
  
  const {
    plan_type,
    plan_label,
    analyses_used,
    analyses_limit,
    analyses_remaining,
    usage_percentage,
    period,
    days_remaining,
    trial_expired,
    display_text
  } = usageInfo;
  
  // Plan-specific styling (3 plans + Founder)
  // PRO = "Investor", PRO_PLUS = "Pro"
  const planStyles = {
    FREE: { icon: BarChart3, color: 'text-slate-600', bg: 'bg-slate-100', border: 'border-slate-300' },
    PRO: { icon: Zap, color: 'text-emerald-600', bg: 'bg-emerald-50', border: 'border-emerald-300' },      // "Investor"
    PRO_PLUS: { icon: Zap, color: 'text-indigo-600', bg: 'bg-indigo-50', border: 'border-indigo-300' },    // "Pro"
    FOUNDER: { icon: Crown, color: 'text-purple-600', bg: 'bg-purple-50', border: 'border-purple-300' }
  };
  
  const style = planStyles[plan_type] || planStyles.FREE;
  const Icon = style.icon;
  
  // Calculate progress bar width
  const progressWidth = Math.min(usage_percentage, 100);
  const progressColor = usage_percentage >= 90 ? 'bg-red-500' : usage_percentage >= 70 ? 'bg-amber-500' : 'bg-emerald-500';
  
  // Warning states
  const isWarning = usage_percentage >= 80;
  const isExpired = trial_expired === true;
  
  // Use display name for plan label
  const displayPlanLabel = plan_label || getPlanDisplayName(plan_type);
  
  return (
    <div 
      className={`flex items-center gap-3 px-3 py-2 rounded-lg ${style.bg} border ${style.border}`}
      data-testid="usage-display"
    >
      <Icon className={`w-4 h-4 ${style.color}`} />
      
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-2">
          <span className={`text-xs font-semibold ${style.color}`}>
            {displayPlanLabel}
          </span>
          
          {isExpired && (
            <span className="text-xs text-red-600 font-medium">
              {lang === 'en' ? 'Expired' : 'Expirado'}
            </span>
          )}
        </div>
        
        {/* Progress bar (not for FOUNDER) */}
        {plan_type !== 'FOUNDER' && (
          <div className="mt-1">
            <div className="h-1.5 bg-slate-200 rounded-full overflow-hidden">
              <div 
                className={`h-full ${progressColor} transition-all duration-300`}
                style={{ width: `${progressWidth}%` }}
              />
            </div>
            <p className="text-xs text-slate-600 mt-1">
              {analyses_used} / {analyses_limit} {lang === 'en' ? 'analyses' : 'análisis'}
              {analyses_remaining > 0 && !isExpired && (
                <span className="text-slate-400 ml-1">
                  ({analyses_remaining} {lang === 'en' ? 'remaining' : 'restantes'})
                </span>
              )}
            </p>
          </div>
        )}
        
        {/* FOUNDER special access message */}
        {plan_type === 'FOUNDER' && (
          <p className="text-xs text-emerald-600 mt-0.5">
            {lang === 'en' ? 'Founder access - Full features' : 'Acceso fundador - Todas las funciones'}
          </p>
        )}
      </div>
    </div>
  );
};

export default UsageDisplay;
